import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, Avatar, TablePagination, useMediaQuery, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import { useNavigate } from 'react-router-dom';
import { School } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState, useCallback } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from '../shared/BulkActions';
import { CSVExport, type ColumnDef } from '../shared/CSVExport';
import { StudentImport } from './StudentImport';
import type { StudentRecord, ClientRecord } from '../shared/types';

const STATUSES = ['Draft', 'Active', 'Inactive'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
};

const STUDENT_COLUMNS: ColumnDef[] = [
  { field: 'legalEntity', header: 'First Name', format: (_: unknown, record: Record<string, unknown>) => (record.legalEntity as Record<string, unknown>)?.firstName as string ?? '' },
  { field: 'legalEntity', header: 'Last Name', format: (_: unknown, record: Record<string, unknown>) => (record.legalEntity as Record<string, unknown>)?.lastName as string ?? '' },
  { field: 'status', header: 'Status' },
];

export const StudentList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { data: allStudents } = useGetList('students', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allClients } = useGetList('clients', { pagination: { page: 1, perPage: 1000 } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const students = allStudents?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allStudents?.length ?? 0,
    active: allStudents?.filter((s: StudentRecord) => s.status === 'Active').length ?? 0,
    inactive: allStudents?.filter((s: StudentRecord) => s.status === 'Inactive').length ?? 0,
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) ids.forEach(id => next.add(id));
      else ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const studentColumns = STUDENT_COLUMNS.map(c => ({
    ...c,
    format: c.field === 'legalEntity' && c.header === 'First Name'
      ? (_: unknown, record: Record<string, unknown>) => `${(record.legalEntity as Record<string, unknown>)?.firstName ?? ''} ${(record.legalEntity as Record<string, unknown>)?.lastName ?? ''}`.trim()
      : undefined,
  }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Students"
          action={<StudentImport />}
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
          ]}
        />
        <BulkActions
          selectedIds={Array.from(selectedIds)}
          resourceName="students"
          selectedCount={selectedIds.size}
          onClearSelection={clearSelection}
          onStatusChange={clearSelection}
          onExportSelected={() => {}}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={filters.clientId || ''}
              label="Client"
              onChange={e => setFilters(prev => ({ ...prev, clientId: e.target.value || undefined }))}
            >
              <MenuItem value="">All</MenuItem>
              {allClients?.map((c: ClientRecord) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {students?.map((student: StudentRecord) => (
            <Paper key={student.id} sx={{ p: 2 }} onClick={() => navigate(`/students/${student.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main', fontSize: 14 }}>
                    {student.legalEntity?.firstName?.[0]}{student.legalEntity?.lastName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{student.legalEntity?.firstName ?? ""} {student.legalEntity?.lastName ?? ""}</Typography>
                    <Typography variant="caption" color="text.secondary">{student.legalEntity?.email || '—'}</Typography>
                  </Box>
                </Box>
                <Chip label={student.status} size="small" color={STATUS_COLORS[student.status]} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Students"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <StudentImport />
            {allStudents && (
              <CSVExport
                data={allStudents}
                columns={STUDENT_COLUMNS}
                filename="students.csv"
                selectedIds={selectedIds.size > 0 ? Array.from(selectedIds) : undefined}
              />
            )}
          </Box>
        }
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
        ]}
      />
      <BulkActions
        selectedIds={Array.from(selectedIds)}
        resourceName="students"
        selectedCount={selectedIds.size}
        onClearSelection={clearSelection}
        onStatusChange={clearSelection}
        onExportSelected={() => {}}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Client</InputLabel>
          <Select
            value={filters.clientId || ''}
            label="Client"
            onChange={e => setFilters(prev => ({ ...prev, clientId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allClients?.map((c: ClientRecord) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <SelectAllCheckbox items={students ?? []} selectedIds={selectedIds} onToggleAll={toggleAll} />
              <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students?.map((student: StudentRecord) => (
              <TableRow
                key={student.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/students/${student.id}/show`)}
              >
                <SelectionCheckbox id={student.id} selected={selectedIds.has(student.id)} onToggle={toggleSelect} />
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 12 }}>
                    {student.legalEntity?.firstName?.[0]}{student.legalEntity?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{student.legalEntity?.firstName ?? ""} {student.legalEntity?.lastName ?? ""}</Typography>
                      <Typography variant="caption" color="text.secondary">{student.legalEntity?.email || '—'}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{student.legalEntity?.email || '—'}</Typography></TableCell>
                <TableCell><Chip label={student.status} size="small" color={STATUS_COLORS[student.status] as ChipColor} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allStudents?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!students || students.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <School sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No students found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
